import { Popover as MantinePopover, PopoverProps, useMantineTheme } from '@mantine/core';
import { forwardRef } from 'react';
import { useMediaQuery } from '@mantine/hooks';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface ThemedPopoverProps extends PopoverProps {
  gradientBorder?: boolean;
  gradientVariant?: GradientVariant;
  responsiveBreakpoint?: number | string;
}

interface PopoverComponent extends React.ForwardRefExoticComponent<ThemedPopoverProps & React.RefAttributes<HTMLDivElement>> {
  Target: typeof MantinePopover.Target;
  Dropdown: typeof MantinePopover.Dropdown;
}

const Popover = forwardRef<HTMLDivElement, ThemedPopoverProps>(
  ({ gradientBorder = false, gradientVariant = 'primary', styles, children, responsiveBreakpoint = 'sm', ...rest }, ref) => {
    const theme = useMantineTheme();
    const gradient = theme.other.gradients[gradientVariant];
    const breakpointValue =
      typeof responsiveBreakpoint === 'number'
        ? responsiveBreakpoint
        : theme.breakpoints?.[responsiveBreakpoint as keyof typeof theme.breakpoints];
    const isSmallScreen = breakpointValue ? useMediaQuery(`(max-width: ${breakpointValue}px)`) : false;
    const commonProps = {
      width: isSmallScreen ? '100%' : rest.width,
      position: isSmallScreen ? 'bottom' : rest.position,
      withinPortal: true,
      transitionProps: { duration: rest.transitionProps?.duration ?? (isSmallScreen ? 100 : 150) },
    };

    if (!gradientBorder) {
      return (
        // @ts-expect-error - Mantine Popover props type mismatch
        <MantinePopover styles={styles} {...commonProps} {...rest} ref={ref as React.RefObject<HTMLDivElement>}>
          {children}
        </MantinePopover>
      );
    }

    return (
      <MantinePopover
        {...rest}
        // @ts-expect-error - Mantine Popover ref type mismatch
        ref={ref as React.RefObject<HTMLDivElement>}
        styles={{
          ...styles,
          dropdown: {
            border: `2px solid ${gradient.from}`,
            ...(styles as any)?.dropdown,
          },
          arrow: {
            borderColor: gradient.from,
            ...(styles as any)?.arrow,
          },
        }}
        {...commonProps}
        {...rest}
      >
        {children}
      </MantinePopover>
    );
  }
) as PopoverComponent;

Popover.displayName = 'Popover';

// Attach subcomponents
Popover.Target = MantinePopover.Target;
Popover.Dropdown = MantinePopover.Dropdown;

export default Popover;
